import React, { useState } from "react"
import { LuChevronDown, LuChevronRight, LuChevronsDownUp, LuChevronsUpDown, LuInfo } from "react-icons/lu"
import styles from "asset/styles/quotation/quotationPreviewV2.module.scss"
import { toTitleCase } from "util/textcase"
import { SectionHeading } from "./quotationPreviewV2Components"
import type { IQuotationPreviewV2DayCost, IQuotationPreviewV2PackagePrice } from "./quotationPreviewV2Types"
import { formatUsd } from "./quotationPreviewV2Util"
import { formatAmount, formatVnd } from "./quotationPreviewV2PricingUtil"

const DayCostTable: React.FC<{ dayCost: IQuotationPreviewV2DayCost }> = ({ dayCost }) => (
	<div className={styles.costTableScroll}>
		<table className={styles.costTable}>
			<thead>
				<tr>
					<th>Category</th>
					<th>Service / Item</th>
					<th>Cost calculation</th>
					<th className={styles.costAmount}>Amount · VND</th>
				</tr>
			</thead>
			<tbody>
				{dayCost.groups.length === 0 ? (
					<tr><td colSpan={4} className={styles.emptyRow}>No costs added for this day</td></tr>
				) : dayCost.groups.map((group) => group.lines.map((line, lineIndex) => (
					<tr
						key={`${group.category}-${lineIndex}`}
						className={[
							lineIndex === 0 ? styles.costGroupStart : "",
							lineIndex === group.lines.length - 1 ? styles.costGroupEnd : "",
						].filter(Boolean).join(" ") || undefined}
					>
						<td className={styles.costCategory}>{lineIndex === 0 ? group.category : ""}</td>
						<td className={styles.costService}>{line.service}</td>
						<td>{line.calculation}</td>
						<td className={styles.costAmount}>{formatAmount(line.amount)}</td>
					</tr>
				)))}
			</tbody>
			<tfoot>
				<tr>
					<td colSpan={3}>Total day cost</td>
					<td className={styles.costAmount}>{formatVnd(dayCost.total)}</td>
				</tr>
			</tfoot>
		</table>
	</div>
)

type IPricingProps = {
	packagePrices: IQuotationPreviewV2PackagePrice[]
	totalPriceText: string
	dayCosts: IQuotationPreviewV2DayCost[]
	versionBadge: string
}

const QuotationPreviewV2Pricing: React.FC<IPricingProps> = ({ packagePrices, totalPriceText, dayCosts, versionBadge }) => {

	const [expandedKeys, setExpandedKeys] = useState<Set<string>>(() => new Set())

	const toggleDay = (key: string) => setExpandedKeys((previous) => {
		const next = new Set(previous)
		if (next.has(key)) next.delete(key)
		else next.add(key)
		return next
	})

	return (
		<section>
			<div className={styles.sectionToolbar}>
				<SectionHeading title="Pricing" versionBadge={versionBadge} />
			</div>

			<div className={styles.tableCard}>
				<table className={`${styles.table} ${styles.fixedRowsTable} ${styles.packageTable}`}>
					<thead>
						<tr>
							<th>Package Pricing</th>
							<th>Amount (USD)</th>
						</tr>
					</thead>
					<tbody>
						{packagePrices.length === 0 ? (
							<tr><td colSpan={2} className={styles.emptyRow}>No pricing data</td></tr>
						) : packagePrices.map((price, priceIndex) => (
							<tr key={`pp-${priceIndex}`}>
								<td>{toTitleCase(price.label)}</td>
								<td>{formatUsd(price.amountUsd)}</td>
							</tr>
						))}
					</tbody>
					<tfoot>
						<tr className={styles.packageTotalRow}>
							<td>Total Package Price</td>
							<td>{totalPriceText}</td>
						</tr>
					</tfoot>
				</table>
			</div>

			<div className={styles.dayCostToolbar}>
				<div className={styles.sectionHeading}>
					<h3 className={styles.dayCostTitle}>Day-wise cost breakdown</h3>
					<span className={styles.versionBadge}>VND</span>
				</div>
				<div className={styles.titleActions}>
					<button
						type="button"
						className={styles.smallOutlineButton}
						onClick={() => setExpandedKeys(new Set(dayCosts.map((dayCost) => dayCost.key)))}
						disabled={dayCosts.length === 0}
					>
						<LuChevronsUpDown size={14} />
						Expand all
					</button>
					<button
						type="button"
						className={styles.smallOutlineButton}
						onClick={() => setExpandedKeys(new Set())}
						disabled={dayCosts.length === 0}
					>
						<LuChevronsDownUp size={14} />
						Collapse all
					</button>
				</div>
			</div>

			<div className={styles.infoNote}>
				<LuInfo size={14} />
				Day costs are in VND. Package selling prices above are in USD.
			</div>

			<div className={styles.dayList}>
				<div className={styles.dayListHeader}>Itinerary days</div>
				{dayCosts.length === 0 ? (
					<div className={[styles.dayRow, styles.emptyRow].join(" ")}>No itinerary days added</div>
				) : dayCosts.map((dayCost) => {
					const flagExpanded = expandedKeys.has(dayCost.key)
					return (
						<div key={dayCost.key} className={flagExpanded ? styles.dayExpanded : undefined}>
							<button
								type="button"
								className={[styles.dayRow, flagExpanded ? styles.dayRowExpanded : ""].filter(Boolean).join(" ")}
								onClick={() => toggleDay(dayCost.key)}
								aria-expanded={flagExpanded}
							>
								{flagExpanded ? <LuChevronDown size={16} /> : <LuChevronRight size={16} />}
								<span className={styles.dayLabel}>{dayCost.label}</span>
								{flagExpanded ? (
									<span className={styles.dayTotalBlock}>
										<span className={styles.dayTotalCaption}>Day total · VND</span>
										<span className={styles.dayTotalValue}>{formatAmount(dayCost.total)}</span>
									</span>
								) : (
									<span className={styles.dayTotal}>{formatVnd(dayCost.total)}</span>
								)}
							</button>
							{flagExpanded && <DayCostTable dayCost={dayCost} />}
						</div>
					)
				})}
			</div>
		</section>
	)
}

export default QuotationPreviewV2Pricing
